import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { NotificationConfigurationCompositeDto } from "@/Common/Api/Services/Dto/NotificationConfigurationDto";
import { Timeframe } from "@/Common/Models/Timeframe";
import { NotificationConfigurationProps } from "./NotificationConfiguration";
import { NotificationConfigurationErrors, NotificationConfigurationInput } from "./NotificationConfiguration.types";
import { useNotificationConfigurationValidation } from "./useNotificationConfigurationValidation";

type NotificationConfigurationHookOpts = Pick<NotificationConfigurationProps, "onClose" | "onSubmit" | "configuration" | "defaultConfiguration">;

type NotificationConfigurationState = NotificationConfigurationInput & { defaultConfiguration: NotificationConfigurationCompositeDto };

type NotificationConfigurationHookResult = {
    errors: NotificationConfigurationErrors;
    values: NotificationConfigurationState;
    formRef: React.MutableRefObject<HTMLFormElement>;
    hasErrors: boolean;
    handleSave: () => void;
    handleClose: () => void;
    handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
    handleBlockCancellationChange: (isChecked: boolean) => void;
    handleUseDefaultCancellationChange: (isChecked: boolean) => void;
    handleUseDefaultNotificationContentChange: (isChecked: boolean) => void;
    handleUseDefaultReminderConfigChange: (isChecked: boolean) => void;
    handleContentChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    handleRemindersChange: (value: Timeframe, name: string) => void;
    handleCancellationChange: (value: Timeframe, name: string) => void;
};

function useNotificationConfiguration(opts: NotificationConfigurationHookOpts): NotificationConfigurationHookResult {
    const { onClose, onSubmit, configuration, defaultConfiguration } = opts;

    const formRef = useRef<HTMLFormElement>();

    const configurationState = useMemo(
        () => ({
            ...configuration,
            defaultConfiguration: defaultConfiguration
        }),
        [configuration, defaultConfiguration]
    );

    const [values, setValues] = useState<NotificationConfigurationState>(configurationState);
    const { errors, hasErrors, validate } = useNotificationConfigurationValidation(values, configurationState);

    function handleCancellationChange(value: Timeframe, name: string): void {
        setValues(currentValues => ({ ...currentValues, [name]: value }));
    }

    function handleRemindersChange(value: Timeframe, name: string): void {
        setValues(currentValues => {
            let reminder = currentValues[name];
            reminder = value;

            return ({ ...currentValues, [name]: reminder });
        });
    }

    function handleContentChange(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
        const { name, value } = e.target;

        setValues(currentValues => ({
            ...currentValues,
            notificationContent: { ...currentValues.notificationContent, [name]: value }
        }));
    }

    function handleBlockCancellationChange(isChecked: boolean): void {
        setValues(currentValues => ({
            ...currentValues,
            enableCancellation: !isChecked
        }));
    }

    function handleUseDefaultCancellationChange(isChecked: boolean): void {
        setValues(currentValues => ({
            ...currentValues,
            useDefaultCancelConfig: isChecked,
            enableCancellation: currentValues.defaultConfiguration.EnableCancellation,
            cancellationLimit: currentValues.defaultConfiguration.CancellationLimit
        }));
    }

    function handleUseDefaultNotificationContentChange(isChecked: boolean): void {
        setValues(currentValues => ({
            ...currentValues,
            useDefaultNotificationContent: isChecked,
            notificationContent: {
                En: currentValues.defaultConfiguration.NotificationContent?.En ?? "",
                Fr: currentValues.defaultConfiguration.NotificationContent?.Fr ?? "",
                Es: currentValues.defaultConfiguration.NotificationContent?.Es ?? ""
            }
        }));
    }

    function handleUseDefaultReminderConfigChange(isChecked: boolean): void {
        setValues(currentValues => ({
            ...currentValues,
            useDefaultReminderConfig: isChecked,
            firstReminder: currentValues.defaultConfiguration.Reminders[0],
            secondReminder: currentValues.defaultConfiguration.Reminders[1]
        }));
    }

    function handleSubmit(e: FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        onSubmit(values);

        handleClose();
    }

    function handleSave(): void {
        validate();

        if (!hasErrors) {
            formRef.current.requestSubmit();
        }
    }

    function handleClose(): void {
        setValues({ ...configurationState });

        onClose?.();
    }

    useEffect(() => {
        setValues(currentValues => ({
            ...currentValues,
            ...configurationState
        }));
    }, [configurationState]);

    return {
        errors,
        values,
        formRef,
        hasErrors,
        handleSave,
        handleClose,
        handleSubmit,
        handleBlockCancellationChange,
        handleUseDefaultCancellationChange,
        handleUseDefaultNotificationContentChange,
        handleUseDefaultReminderConfigChange,
        handleContentChange,
        handleRemindersChange,
        handleCancellationChange
    };
}

export { useNotificationConfiguration };
